// Shared money + date formatting (Session AEH, FIX-1 A4) so the office and portal stop drifting
// between a dozen local copies (some printed "GBP 1,234", some "£1234.00", some ungrouped).
//
// gbp:      whole pounds, grouped         -> "£1,234"
// gbpExact: pence precision, grouped      -> "£1,234.56"
// formatDate: the platform day format     -> "19 Jul 2026"
//
// SESSION AUJ (design system Session 2, spec 3.6): THE DATE HELPERS. Every office date reads one of
// the functions below instead of its own toLocaleDateString shape (about 150 call sites used 40
// distinct option shapes, and `fmtDate` alone was defined 51 times). All of them:
//   - return "-" for null, undefined, an empty string or an unparseable value;
//   - are PINNED TO Europe/London, so the same instant reads the same on every machine. A date-only
//     field arrives as UTC midnight, and London is UTC+0 or UTC+1, never behind UTC, so UTC midnight
//     is always the same calendar day in London; an instant (a sent-at, an arrival) is shown on its
//     London day. The browser's own zone (what formatDate did before) is the only reading that can
//     show the wrong day, for anyone outside the UK.
//   - are BUILT FROM PARTS with fixed English names, not from toLocaleDateString: modern ICU prints
//     September as "Sept" in en-GB (Node 22 and current Chrome) and older engines print "Sep", so a
//     locale-formatted string would differ by browser. These print "5 Sep 2026" everywhere.
// OUT OF SCOPE (left alone): toLocaleString("en-GB") on numbers (money and counts: use gbp/gbpExact),
// lib/mailWorkspace.ts, lib/londonTime.ts and lib/privacyNotice.ts (London wall-clock parts for logic
// rather than display), a live clock that shows seconds, and any server-rendered text.
//
// A null/undefined/NaN input renders "-" (never "£NaN" or an empty cell).

export function gbp(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "-";
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

export function gbpExact(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "-";
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export type DateInput = string | Date | number | null | undefined;

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

interface LondonParts {
  year: number;
  month: number; // 1 to 12
  day: number;
  hour: number;
  minute: number;
  weekday: number; // 0 = Sunday
}

let londonFormat: Intl.DateTimeFormat | null = null;

function londonParts(d: DateInput): LondonParts | null {
  if (d == null || d === "") return null;
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  londonFormat ??= new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h23",
  });
  const parts = londonFormat.formatToParts(date);
  const num = (type: string): number => Number(parts.find((p) => p.type === type)?.value);
  const year = num("year");
  const month = num("month");
  const day = num("day");
  return {
    year,
    month,
    day,
    hour: num("hour") % 24,
    minute: num("minute"),
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
  };
}

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** "5 Sep 2026" */
export function formatDate(d: DateInput): string {
  const p = londonParts(d);
  return p ? `${p.day} ${MONTHS_SHORT[p.month - 1]} ${p.year}` : "-";
}

/** "5 Sep 2026, 14:05" */
export function formatDateTime(d: DateInput): string {
  const p = londonParts(d);
  return p ? `${p.day} ${MONTHS_SHORT[p.month - 1]} ${p.year}, ${pad2(p.hour)}:${pad2(p.minute)}` : "-";
}

/** "14:05" (24 hour) */
export function formatTime(d: DateInput): string {
  const p = londonParts(d);
  return p ? `${pad2(p.hour)}:${pad2(p.minute)}` : "-";
}

/** "5 Sep": calendar cells and compact lists */
export function formatDayMonth(d: DateInput): string {
  const p = londonParts(d);
  return p ? `${p.day} ${MONTHS_SHORT[p.month - 1]}` : "-";
}

/** "Sat 5 Sep": schedule column headers */
export function formatWeekdayShort(d: DateInput): string {
  const p = londonParts(d);
  return p ? `${WEEKDAYS_SHORT[p.weekday]} ${p.day} ${MONTHS_SHORT[p.month - 1]}` : "-";
}

/** "Saturday 5 September 2026": day headings and confirm dialogs */
export function formatDateLong(d: DateInput): string {
  const p = londonParts(d);
  return p ? `${WEEKDAYS_LONG[p.weekday]} ${p.day} ${MONTHS_LONG[p.month - 1]} ${p.year}` : "-";
}

/**
 * "5 September 2026" read in UTC, never the viewer's zone and never London. For the one place a
 * date must match a canonical UTC value sent elsewhere (the auditor's "Access expires" line, which
 * the server's auditorDateDisplay prints in UTC and the auditor was emailed).
 */
export function formatDateLongUtc(d: DateInput): string {
  if (d == null || d === "") return "-";
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return "-";
  return `${date.getUTCDate()} ${MONTHS_LONG[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/** "September 2026" */
export function formatMonthYear(d: DateInput): string {
  const p = londonParts(d);
  return p ? `${MONTHS_LONG[p.month - 1]} ${p.year}` : "-";
}

/** "05/09/2026": only where a numeric date is already the convention (dashboard checked-at lines). */
export function formatDateNumeric(d: DateInput): string {
  const p = londonParts(d);
  return p ? `${pad2(p.day)}/${pad2(p.month)}/${p.year}` : "-";
}

/** "2026-09-05": a sort or group key, never displayed (replaces the en-CA toLocaleDateString trick). */
export function londonDateKey(d: DateInput): string {
  const p = londonParts(d);
  return p ? `${p.year}-${pad2(p.month)}-${pad2(p.day)}` : "-";
}
